import { useTranslation } from 'react-i18next';
import { HiPencil, HiCheck } from 'react-icons/hi2';
import type { InlineEditProps } from './types';
import { useInlineEdit } from './useInlineEdit';
import InlineEditInput from './InlineEditInput';
import {
  InlineEditWrapper,
  DisplayValue,
  EditPencilButton,
  SuccessIcon,
  InlineError,
  SavingSpinner,
  EditContainer,
} from './InlineEdit.styled';

export default function InlineEdit({
  value,
  onSave,
  variant = 'text',
  options,
  placeholder,
  renderDisplay,
  validate,
  required,
  ariaLabel,
  disabled = false,
  min,
  max,
  step,
  colorPresets,
  emptyText,
}: InlineEditProps) {
  const { t } = useTranslation();
  const resolvedEmptyText = emptyText ?? '---';

  const {
    state,
    editValue,
    setEditValue,
    error,
    showSuccess,
    inputRef,
    isImmediate,
    startEditing,
    handleKeyDown,
    handleBlur,
    handleImmediateChange,
  } = useInlineEdit({ value, onSave, variant, validate, required });

  const isEmpty = value === null || value === undefined || value === '';
  const isSaving = state === 'saving';

  // Immediate variants (select, color, switch): always show input directly
  if (isImmediate) {
    return (
      <InlineEditWrapper>
        <InlineEditInput
          variant={variant}
          value={editValue}
          onChange={handleImmediateChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {}}
          inputRef={inputRef}
          disabled={disabled || isSaving}
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          options={options}
          min={min}
          max={max}
          step={step}
          colorPresets={colorPresets}
          isImmediate
        />
        {isSaving && <SavingSpinner />}
        {showSuccess && (
          <SuccessIcon>
            <HiCheck />
          </SuccessIcon>
        )}
        {error && <InlineError>{error}</InlineError>}
      </InlineEditWrapper>
    );
  }

  // Display mode
  if (state === 'display') {
    const displayContent = renderDisplay
      ? renderDisplay(value)
      : isEmpty
        ? resolvedEmptyText
        : String(value);

    return (
      <InlineEditWrapper>
        <DisplayValue
          $muted={isEmpty}
          $flash={showSuccess}
          onClick={disabled ? undefined : startEditing}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              startEditing();
            }
          }}
          aria-label={`${ariaLabel}: ${isEmpty ? resolvedEmptyText : String(value)}`}
        >
          {displayContent}
        </DisplayValue>
        {showSuccess && (
          <SuccessIcon>
            <HiCheck />
          </SuccessIcon>
        )}
        {!disabled && (
          <EditPencilButton
            onClick={startEditing}
            type="button"
            aria-label={t('inlineEdit.edit')}
          >
            <HiPencil />
          </EditPencilButton>
        )}
      </InlineEditWrapper>
    );
  }

  // Editing / Saving mode
  return (
    <InlineEditWrapper>
      <EditContainer>
        <InlineEditInput
          variant={variant}
          value={editValue}
          onChange={setEditValue}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          inputRef={inputRef}
          disabled={isSaving}
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          options={options}
          min={min}
          max={max}
          step={step}
          colorPresets={colorPresets}
          isImmediate={false}
        />
        {error && <InlineError>{error}</InlineError>}
      </EditContainer>
      {isSaving && <SavingSpinner />}
    </InlineEditWrapper>
  );
}
